// Fallback base stats for era characters when the server sends zero stats.
// Values are in RAW format (pre-conversion), matching the rawStats format:
//   stat 1 = Health, 5 = Speed, 6 = Physical Damage, 7 = Special Damage,
//   8 = Defense (Armor), 9 = Resistance, 14 = Physical Crit Chance,
//   15 = Special Crit Chance, 16 = Crit Damage, 17 = Potency, 18 = Tenacity,
//   28 = Protection, 37 = Accuracy, 39 = Critical Avoidance
//
// Sourced from swgoh.gg at max gear/relic. Display values are converted to
// raw format using the inverse of the UNIT_STAT_FORMATTERS conversions.
//
// These are temporary until the server provides proper stats for era characters.

interface RawStat
{
    stat: number;
    amount: number;
}

const eraUnitFallbackStats: Map<string, RawStat[]> = new Map([
    // https://swgoh.gg/units/inquisitor-barriss/
    ["INQUISITORBARRISS", [
        { stat: 1, amount: 75085 },   // Health
        { stat: 28, amount: 57732 },  // Protection
        { stat: 5, amount: 159 },     // Speed
        { stat: 16, amount: 1.5 },    // Crit Damage (150% → 1.5)
        { stat: 17, amount: 0.43 },   // Potency (43% → 0.43)
        { stat: 18, amount: 0.90 },   // Tenacity (90% → 0.90)
        { stat: 6, amount: 4722 },    // Physical Damage
        { stat: 7, amount: 8272 },    // Special Damage
        { stat: 14, amount: 901 },    // Physical Crit Chance (47.54% → (0.4754-0.1)*2400)
        { stat: 15, amount: 295 },    // Special Crit Chance (22.29% → (0.2229-0.1)*2400)
        { stat: 8, amount: 635 },     // Defense/Armor (49.9% at lv85 → 0.499*637.5/(1-0.499))
        { stat: 9, amount: 635 },     // Resistance (49.9% at lv85)
        { stat: 37, amount: 0 },      // Accuracy (0%)
        { stat: 39, amount: 432 },    // Critical Avoidance (18% → 0.18*2400)
    ]],
    // https://swgoh.gg/units/stormtrooper-luke/
    ["STORMTROOPERLUKE", [
        { stat: 1, amount: 57481 },   // Health
        { stat: 28, amount: 58947 },  // Protection
        { stat: 5, amount: 194 },     // Speed
        { stat: 16, amount: 1.5 },    // Crit Damage (150%)
        { stat: 17, amount: 0.18 },   // Potency (18%)
        { stat: 18, amount: 1.48 },   // Tenacity (148%)
        { stat: 6, amount: 4673 },    // Physical Damage
        { stat: 7, amount: 7666 },    // Special Damage
        { stat: 14, amount: 1007 },   // Physical Crit Chance (51.96%)
        { stat: 15, amount: 170 },    // Special Crit Chance (17.08%)
        { stat: 8, amount: 1030 },    // Defense/Armor (61.8% at lv85)
        { stat: 9, amount: 863 },     // Resistance (57.5% at lv85)
        { stat: 37, amount: 216 },    // Accuracy (18%)
        { stat: 39, amount: 432 },    // Critical Avoidance (18%)
    ]],
    // https://swgoh.gg/units/ig-90/
    ["IG90", [
        { stat: 1, amount: 59240 },   // Health
        { stat: 28, amount: 62219 },  // Protection
        { stat: 5, amount: 179 },     // Speed
        { stat: 16, amount: 1.68 },   // Crit Damage (168%)
        { stat: 17, amount: 0.55 },   // Potency (55%)
        { stat: 18, amount: 0.63 },   // Tenacity (63%)
        { stat: 6, amount: 4144 },    // Physical Damage
        { stat: 7, amount: 5192 },    // Special Damage
        { stat: 14, amount: 1415 },   // Physical Crit Chance (68.96%)
        { stat: 15, amount: 462 },    // Special Crit Chance (29.25%)
        { stat: 8, amount: 739 },     // Defense/Armor (53.7% at lv85)
        { stat: 9, amount: 536 },     // Resistance (45.7% at lv85)
        { stat: 37, amount: 0 },      // Accuracy (0%)
        { stat: 39, amount: 0 },      // Critical Avoidance (0%)
    ]],
    // https://swgoh.gg/units/asajj-dark-disciple/
    ["ASAJJDARKDISCIPLE", [
        { stat: 1, amount: 48863 },   // Health
        { stat: 28, amount: 57732 },  // Protection
        { stat: 5, amount: 158 },     // Speed
        { stat: 16, amount: 1.68 },   // Crit Damage (168%)
        { stat: 17, amount: 0.32 },   // Potency (32%)
        { stat: 18, amount: 0.58 },   // Tenacity (58%)
        { stat: 6, amount: 6951 },    // Physical Damage
        { stat: 7, amount: 5726 },    // Special Damage
        { stat: 14, amount: 1979 },   // Physical Crit Chance (92.46%)
        { stat: 15, amount: 492 },    // Special Crit Chance (30.5%)
        { stat: 8, amount: 632 },     // Defense/Armor (49.8% at lv85)
        { stat: 9, amount: 250 },     // Resistance (28.2% at lv85)
        { stat: 37, amount: 0 },      // Accuracy (0%)
        { stat: 39, amount: 0 },      // Critical Avoidance (0%)
    ]],
    // https://swgoh.gg/units/yoda-chewbacca/
    ["YODACHEWBACCA", [
        { stat: 1, amount: 54678 },   // Health
        { stat: 28, amount: 57732 },  // Protection
        { stat: 5, amount: 186 },     // Speed
        { stat: 16, amount: 1.5 },    // Crit Damage (150%)
        { stat: 17, amount: 0.38 },   // Potency (38%)
        { stat: 18, amount: 0.91 },   // Tenacity (91%)
        { stat: 6, amount: 4843 },    // Physical Damage
        { stat: 7, amount: 8408 },    // Special Damage
        { stat: 14, amount: 757 },    // Physical Crit Chance (41.54%)
        { stat: 15, amount: 250 },    // Special Crit Chance (20.42%)
        { stat: 8, amount: 917 },     // Defense/Armor (59% at lv85)
        { stat: 9, amount: 852 },     // Resistance (57.2% at lv85)
        { stat: 37, amount: 216 },    // Accuracy (18%)
        { stat: 39, amount: 432 },    // Critical Avoidance (18%)
    ]],
    // https://swgoh.gg/units/maul-hate-fueled/
    ["MAULHATEFUELED", [
        { stat: 1, amount: 72579 },   // Health
        { stat: 28, amount: 62219 },  // Protection
        { stat: 5, amount: 168 },     // Speed
        { stat: 16, amount: 1.5 },    // Crit Damage (150%)
        { stat: 17, amount: 0.33 },   // Potency (33%)
        { stat: 18, amount: 0.36 },   // Tenacity (36%)
        { stat: 6, amount: 6906 },    // Physical Damage
        { stat: 7, amount: 5916 },    // Special Damage
        { stat: 14, amount: 980 },    // Physical Crit Chance (50.83% → (0.5083-0.1)*2400)
        { stat: 15, amount: 0 },      // Special Crit Chance (10% → (0.10-0.1)*2400 = 0)
        { stat: 8, amount: 702 },     // Defense/Armor (52.4% at lv85 → 0.524*637.5/0.476)
        { stat: 9, amount: 441 },     // Resistance (40.9% at lv85 → 0.409*637.5/0.591)
        { stat: 37, amount: 216 },    // Accuracy (18%)
        { stat: 39, amount: 0 },      // Critical Avoidance (0%)
    ]],
    // https://swgoh.gg/units/vader-duels-end/
    ["VADERDUELSEND", [
        { stat: 1, amount: 69481 },   // Health
        { stat: 28, amount: 62219 },  // Protection
        { stat: 5, amount: 159 },     // Speed
        { stat: 16, amount: 1.5 },    // Crit Damage (150%)
        { stat: 17, amount: 0.43 },   // Potency (43%)
        { stat: 18, amount: 0.63 },   // Tenacity (63%)
        { stat: 6, amount: 7198 },    // Physical Damage
        { stat: 7, amount: 6172 },    // Special Damage
        { stat: 14, amount: 1087 },   // Physical Crit Chance (55.29%)
        { stat: 15, amount: 0 },      // Special Crit Chance (10% → (0.10-0.1)*2400 = 0)
        { stat: 8, amount: 760 },     // Defense/Armor (54.4% at lv85)
        { stat: 9, amount: 374 },     // Resistance (37% at lv85)
        { stat: 37, amount: 216 },    // Accuracy (18%)
        { stat: 39, amount: 0 },      // Critical Avoidance (0%)
    ]],
]);

export function getEraUnitFallbackStats(baseId: string): RawStat[] | undefined
{
    return eraUnitFallbackStats.get(baseId);
}
